export const MAX_BACKGROUND_IMAGE_BYTES = 10e6; // 10 MB
// How far the panel surface is shaded from the selected background colour when
// panel-match is on (lightened over a dark background, darkened over a light one).
const PANEL_SHADE_AMOUNT = 0.12;

export const ZOOM_MIN = 75;
export const ZOOM_MAX = 300;
export const ZOOM_STEP = 25;
// Deliberately not ZOOM_MIN: a dashboard saved before this setting existed has no stored
// zoom, and has to keep rendering exactly as it does today rather than at the lowest
// zoom anyone can now pick.
export const DEFAULT_ZOOM = 100;
// New gamified dashboards start zoomed because they are usually read from across a room.
export const DEFAULT_GAMIFIED_ZOOM = 175;

export const DEFAULT_APPEARANCE = {
  backgroundType: "color",
  // null so the picker and dashboard fall back to the active theme's background
  // colour rather than a hardcoded value.
  backgroundColor: null,
  backgroundGradient: null,
  backgroundImage: null,
  // When panelMatch is on, panels track a shade of the background colour. When off,
  // panelColor (a hex string) is used as the explicit panel surface colour.
  panelMatch: true,
  panelColor: null,
  zoom: DEFAULT_ZOOM
};

// Returns a CSS zoom multiplier (1 = unzoomed). Values are clamped because they come
// from persisted JSON, which may predate or exceed the slider's range. An absent zoom
// resolves to unzoomed rather than to DEFAULT_ZOOM's percentage so that dashboards saved
// before the setting existed are left alone.
export const resolveZoom = (appearance) => {
  const percent = appearance == null ? null : appearance.zoom;
  if (!Number.isFinite(percent)) {
    return 1;
  }
  return Math.min(Math.max(percent, ZOOM_MIN), ZOOM_MAX) / 100;
};

export const buildGradientCss = ({from, to, angle}) =>
  `linear-gradient(${angle}deg, ${from}, ${to})`;

export const isLightColor = (hex) => {
  const c = (hex || "").replace("#", "");
  const expanded = c.length === 3 ? c.split("").map(x => x + x).join("") : c;
  if (expanded.length !== 6) {
    return false;
  }
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// suppressImage skips loading/applying an uploaded background image (falling back to the
// gradient/solid colour, or nothing). Used for scheduled reports, which must not pull in
// the image asset.
export const resolveBackgroundStyle = (appearance, {suppressImage = false} = {}) => {
  if (!appearance) {
    return {};
  }
  const {backgroundType, backgroundColor, backgroundGradient, backgroundImage} = appearance;

  if (!suppressImage && backgroundType === "image" && backgroundImage && backgroundImage.url) {
    if (backgroundImage.fit === "tile") {
      return {
        backgroundImage: `url("${backgroundImage.url}")`,
        backgroundSize: "auto",
        backgroundPosition: "top left",
        backgroundRepeat: "repeat"
      };
    }
    return {
      backgroundImage: `url("${backgroundImage.url}")`,
      backgroundSize: backgroundImage.fit === "stretch" ? "100% 100%" : "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat"
    };
  }
  if (backgroundGradient) {
    return {background: buildGradientCss(backgroundGradient)};
  }
  if (backgroundColor) {
    return {background: backgroundColor};
  }
  return {};
};

// Shade a hex colour toward white or black by `amount` (0..1). Returns the input
// unchanged if it isn't a parseable hex colour.
export const tintHexColor = (baseHex, towardWhite, amount = PANEL_SHADE_AMOUNT) => {
  const c = (baseHex || "").replace("#", "");
  const expanded = c.length === 3 ? c.split("").map(x => x + x).join("") : c;
  if (expanded.length !== 6) {
    return baseHex;
  }
  const target = towardWhite ? 255 : 0;
  const mix = (channel) => Math.round(channel * (1 - amount) + target * amount);
  const toHex = (n) => n.toString(16).padStart(2, "0");
  const r = mix(parseInt(expanded.slice(0, 2), 16));
  const g = mix(parseInt(expanded.slice(2, 4), 16));
  const b = mix(parseInt(expanded.slice(4, 6), 16));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// The panel surface colour derived from the selected background: a shade of the
// chosen colour (lighter over a dark background, darker over a light one) so panels
// sit nicely against it. Used both to render matched panels and to seed the panel
// colour picker. Returns null when no reference colour is available.
export const getMatchedPanelColor = (appearance) => {
  if (!appearance) {
    return null;
  }
  const referenceColor = appearance.backgroundGradient
    ? appearance.backgroundGradient.from
    : appearance.backgroundColor;
  if (!referenceColor) {
    return null;
  }
  return tintHexColor(referenceColor, !isLightColor(referenceColor));
};

// Resolve the panel surface colour to render. When panel-match is on this tracks the
// background; otherwise the explicitly picked panelColor is used. Returns null when
// neither applies (panels fall back to the default card colour).
export const resolvePanelSurfaceColor = (appearance) => {
  if (!appearance) {
    return null;
  }
  if (appearance.panelMatch) {
    return getMatchedPanelColor(appearance);
  }
  return appearance.panelColor || null;
};

export const validateBackgroundImageFile = (file) => {
  const validTypes = ["image/png", "image/jpeg", "image/gif"];
  if (!file || !validTypes.includes(file.type)) {
    return {valid: false, error: "You can only upload PNG (.png), JPEG (.jpg, .jpeg) or GIF (.gif) files."};
  }
  if (file.size > MAX_BACKGROUND_IMAGE_BYTES) {
    return {valid: false, error: "You can only upload images up to 10MB."};
  }
  return {valid: true, error: null};
};
